import AnimationController from '../../assets/js/controllers/animation';
import ResizeController from '../../assets/js/controllers/resize';
import ScrollController from '../../assets/js/controllers/scroll';

import ParallaxEvent from './parallax-event';
import Platform from '../../assets/js/constants/platform';

import { getRootOffsetBounds, validateParamBoolean } from '../../assets/js/helpers';

export default class Parallax {

	static selector = '[data-parallax]';

	// class variables

	el = {};

	config = {
		slide: {
			ratio: 0.65,
		},
		isReversed: false,
		isMobileEnabled: false
	};

	controllers = {
		animation: null,
		resize: null
	};

	coords = {
		container: {
			bounds: null
		},
		inner: {
			bounds: null
		}
	};

	flags = {
		isEnabled: true,
		isPlatformEnabled: false,
		isInViewport: false,
		isSticky: false,
		willStick: false
	};

	states = {
		directionModifier: -1,
		ratio: 0,
		slide: 0,
		inner: {
			height: 0,
			translateY: 0
		}
	};

	timeouts = {
		unstick: null,
		collapse: null
	};

	// constructor

	constructor(container) {
		this.el = {
			container,
			inner: container.querySelector('[data-parallax-inner]')
		};

		this.config.isReversed = validateParamBoolean(this.el.container, 'data-parallax-reversed');
		this.config.isMobileEnabled = validateParamBoolean(this.el.container, 'data-parallax-mobile');

		if (this.config.isReversed) {
			this.states.directionModifier = 1;
		}

		this.initialize();
	}

	// init

	initialize() {
		this.enable();
		this.#addEventListeners();
	}

	// event listeners

	#addEventListeners = () => {
		this.el.container.addEventListener(ParallaxEvent.ENABLE, this.#onEnableEvent);
		this.el.container.addEventListener(ParallaxEvent.DISABLE, this.#onDisableEvent);
		this.el.container.addEventListener(ParallaxEvent.RENDER, this.#onRenderEvent);

		// controllers

		this.controllers.resize = ResizeController.set({
			update: this.#onResizeUpdate,
			render: this.#onResizeRender
		});

		this.measureResize();
	}

	#removeEventListeners = () => {
		this.el.container.removeEventListener(ParallaxEvent.ENABLE, this.#onEnableEvent);
		this.el.container.removeEventListener(ParallaxEvent.DISABLE, this.#onDisableEvent);
		this.el.container.removeEventListener(ParallaxEvent.RENDER, this.#onRenderEvent);

		// controllers

		AnimationController.clear(this.controllers.animation);
		ResizeController.clear(this.controllers.resize);
	}

	// listener methods

	#onEnableEvent = () => {
		this.enable();
	}

	#onDisableEvent = () => {
		this.disable();
	}

	#onRenderEvent = () => {
		this.render();
	}

	// controller methods

	#onAnimationUpdate = () => this.measureAnimation();
	#onAnimationRender = () => {
		this.renderAnimation();
	}

	#onResizeUpdate = () => this.measureResize();
	#onResizeRender = () => {
		this.adjustResize();
	}

	//

	measureAnimation() {
		this.flags.isInViewport = false;

		if (this.coords.container.top <= ScrollController.easedBottom && this.coords.container.bottom >= ScrollController.eased) {
			this.flags.isInViewport = true;

			if (this.config.isReversed) {
				this.states.ratio = 1 + (ScrollController.eased - this.coords.container.bottom) / this.coords.container.height;
			} else {
				this.states.ratio = 0 - (ScrollController.eased - this.coords.container.bottom) / this.coords.container.height;
			}

			this.states.inner.translateY = 0 - this.states.slide * this.states.ratio;
		}

		return this.flags.isInViewport;
	}

	renderAnimation() {
		if (this.flags.isEnabled) {
			this.render();
		}
	}

	//

	measureResize() {
		this.coords.container = getRootOffsetBounds(this.el.container, document.body);
		this.coords.inner = getRootOffsetBounds(this.el.inner);
		this.states.slide = ScrollController.viewportHeight * this.config.slide.ratio;
		this.states.inner.height = this.coords.container.height + this.states.slide;

		if (ResizeController.platform === Platform.DESKTOP || this.config.isMobileEnabled) {
			this.flags.isPlatformEnabled = true;
			if (!this.controllers.animation) {
				this.controllers.animation = AnimationController.set({
					update: this.#onAnimationUpdate,
					render: this.#onAnimationRender
				});
			}
		} else {
			this.flags.isPlatformEnabled = false;
			AnimationController.clear(this.controllers.animation);
			this.controllers.animation = null;
		}

		return true;
	}

	adjustResize() {
		if (this.flags.isPlatformEnabled) {
			this.el.container.style.overflow = 'hidden';
			this.el.inner.style.height = `${this.states.inner.height}px`;
			this.render();
		} else {
			this.removeStyles();
		}
	}

	//

	render() {
		if (this.flags.isPlatformEnabled) {
			this.el.inner.style.transform = `translateY(${this.states.inner.translateY}px)`;
		}
	}

	// make sticky / unstick (desktop only)

	stick() {
		if (!this.flags.isSticky) {
			this.flags.isSticky = true;

			ScrollController.stick(this.el.container);
			this.el.container.classList.add('is-sticky');

			AnimationController.one({
				update: () => true,
				render: () => {
					this.el.container.classList.add('is-sticky-in');
				}
			});
		}
	}

	unstick() {
		if (this.flags.isSticky) {
			this.flags.isSticky = false;

			this.el.container.classList.remove('is-sticky-in');

			this.timeouts.unstick = setTimeout(() => {
				this.timeouts.unstick = null;
				this.el.container.classList.remove('is-sticky', 'is-sticky-out');
				ScrollController.unstick(this.el.container);
			}, 400);
		}
	}

	//

	enable() {
		this.flags.isEnabled = true;
	}

	disable() {
		this.flags.isEnabled = false;
	}

	//

	checkStatus() {
		if (this.flags.isSticky !== this.flags.willStick) {
			if (this.flags.willStick) {
				this.stick();
			} else {
				this.unstick();
			}
		}
	}

	removeStyles() {
		this.el.container.style.overflow = '';
		this.el.inner.style.height = '';
		this.el.inner.style.transform = '';
	}

	// destroy

	destroy() {
		this.removeStyles();
		this.#removeEventListeners();
	}

}
